import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { loadDraft, saveDraft, clearDraft } from "../../lib/onboarding-storage";
import { LANGUAGES } from "../../lib/languages";
import { TIMEZONES, detectTimezone } from "../../lib/timezones";
import { CURRENCIES } from "../../lib/currencies";
import OnboardingLayout from "./OnboardingLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import MultiSelectChips from "../../components/MultiSelectChips";
import {
  Loader2,
  Save,
  ArrowRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const DEFAULT_FUNCTIONS = [
  "Strategy", "Finance", "Operations", "HR", "Marketing", "Sales",
  "Technology", "Supply Chain", "Risk", "Governance",
  "Customer Experience", "General Business Health",
];
const DEFAULT_INDUSTRIES = [
  "Financial Services", "Manufacturing", "Retail", "Healthcare",
  "Technology", "Energy", "Real Estate", "Hospitality",
  "Education", "Telecom", "Logistics", "Professional Services",
  "Cross-industry",
];

function SectionHeader({ label, hint }) {
  return (
    <div className="mb-4">
      <div className="text-xs font-sans-ui uppercase tracking-[0.18em] text-accent/90">
        {label}
      </div>
      {hint && (
        <div className="mt-1 text-xs text-muted-foreground/80">{hint}</div>
      )}
    </div>
  );
}

export default function Step2Practice() {
  const nav = useNavigate();
  const { user, setUser } = useAuth();

  const draft = loadDraft("step2") || {};
  const [title, setTitle] = useState(draft.title || "");
  const [consultancy, setConsultancy] = useState(
    draft.consultancy || user?.company || ""
  );
  const [primaryFunction, setPrimaryFunction] = useState(
    draft.primaryFunction || ""
  );
  const [industries, setIndustries] = useState(draft.industries || []);
  const [homeMarket, setHomeMarket] = useState(
    draft.homeMarket || user?.primary_country || ""
  );
  const [additionalMarkets, setAdditionalMarkets] = useState(
    draft.additionalMarkets || []
  );
  const [primaryLanguage, setPrimaryLanguage] = useState(
    draft.primaryLanguage || user?.primary_language || "en"
  );
  const [additionalLanguages, setAdditionalLanguages] = useState(
    draft.additionalLanguages || []
  );
  const [timeZone, setTimeZone] = useState(
    draft.timeZone || detectTimezone()
  );
  const [currency, setCurrency] = useState(draft.currency || "USD");
  const [countries, setCountries] = useState([]);
  const [showLocale, setShowLocale] = useState(false);
  const [saving, setSaving] = useState(false);

  const additionalLanguageOptions = useMemo(
    () => LANGUAGES.filter((l) => l.code !== primaryLanguage).map((l) => l.code),
    [primaryLanguage]
  );

  useEffect(() => {
    api.get("/catalog").then((r) => {
      setCountries(r.data?.countries || []);
    });
  }, []);

  useEffect(() => {
    saveDraft("step2", {
      title, consultancy, primaryFunction, industries, homeMarket,
      additionalMarkets, primaryLanguage, additionalLanguages, timeZone, currency,
    });
  }, [
    title, consultancy, primaryFunction, industries, homeMarket,
    additionalMarkets, primaryLanguage, additionalLanguages, timeZone, currency,
  ]);

  const filledFraction = (() => {
    const req = [
      title,
      primaryFunction,
      industries.length > 0,
      homeMarket,
      primaryLanguage,
      timeZone,
      currency,
    ];
    const done = req.filter(Boolean).length;
    return Math.round((done / req.length) * 100);
  })();

  const canSubmit =
    title.trim() &&
    primaryFunction &&
    industries.length > 0 &&
    homeMarket &&
    primaryLanguage &&
    timeZone &&
    currency;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      const payload = {
        professional_title: title.trim(),
        company: consultancy.trim() || user?.company || "",
        functions: [primaryFunction],
        industries,
        primary_country: homeMarket,
        countries: [
          homeMarket,
          ...additionalMarkets.filter((m) => m !== homeMarket),
        ],
        additional_countries: additionalMarkets.filter(
          (m) => m !== homeMarket
        ),
        primary_language: primaryLanguage,
        additional_languages: additionalLanguages.filter(
          (l) => l !== primaryLanguage
        ),
        time_zone: timeZone,
        currency,
      };
      const { data } = await api.put("/consultants/me", payload);
      setUser({ ...(data || user), role: "consultant" });
      clearDraft("step2");
      nav("/onboarding/welcome");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Couldn't save your practice profile"
      );
    } finally {
      setSaving(false);
    }
  };

  const supportedCountries = countries.length
    ? countries
    : [homeMarket].filter(Boolean);

  return (
    <OnboardingLayout
      currentStep="practice"
      eyebrow="Step 2 · Practice"
      title="Tell us about your practice"
      subtitle="This tunes the diagnostics, benchmarks and outreach copy to your market. Smart defaults are already in place."
      estimatedMinutes="~2 min"
    >
      <div className="mb-8">
        <div className="flex items-center justify-between text-xs font-sans-ui uppercase tracking-widest text-muted-foreground mb-2">
          <span>Practice profile</span>
          <span data-testid="practice-progress-pct">
            {filledFraction}% complete
          </span>
        </div>
        <div className="h-1 rounded-full bg-border/60 overflow-hidden">
          <div
            className="h-full bg-accent transition-all"
            style={{ width: `${filledFraction}%` }}
          />
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        className="space-y-10"
        data-testid="onboarding-step2-form"
      >
        {/* You */}
        <section>
          <SectionHeader label="Who you are" />
          <div className="space-y-5">
            <div>
              <Label className="text-sm">Professional title</Label>
              <Input
                data-testid="practice-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-2"
                placeholder="Managing Partner, CFO Advisor…"
              />
            </div>
            <div>
              <Label className="text-sm">
                Consultancy name{" "}
                <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                data-testid="practice-consultancy"
                value={consultancy}
                onChange={(e) => setConsultancy(e.target.value)}
                className="mt-2"
                placeholder="Independent, or firm name"
              />
            </div>
          </div>
        </section>

        {/* Focus */}
        <section>
          <SectionHeader
            label="What you focus on"
            hint="Powers question selection and benchmark cohorts."
          />
          <div className="space-y-5">
            <div>
              <Label className="text-sm">Primary business function</Label>
              <Select
                value={primaryFunction}
                onValueChange={setPrimaryFunction}
              >
                <SelectTrigger className="mt-2" data-testid="practice-function">
                  <SelectValue placeholder="Where do you deliver the most value?" />
                </SelectTrigger>
                <SelectContent>
                  {DEFAULT_FUNCTIONS.map((f) => (
                    <SelectItem key={f} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Industries served</Label>
              <div className="mt-2">
                <MultiSelectChips
                  value={industries}
                  onChange={setIndustries}
                  options={DEFAULT_INDUSTRIES}
                  testid="practice-industries"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Markets */}
        <section>
          <SectionHeader label="Where you operate" />
          <div className="space-y-5">
            <div>
              <Label className="text-sm">Home market</Label>
              <Select value={homeMarket} onValueChange={setHomeMarket}>
                <SelectTrigger
                  className="mt-2"
                  data-testid="practice-home-market"
                >
                  <SelectValue placeholder="Choose your primary country" />
                </SelectTrigger>
                <SelectContent>
                  {supportedCountries.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">
                Additional markets{" "}
                <span className="text-muted-foreground">(optional)</span>
              </Label>
              <div className="mt-2">
                <MultiSelectChips
                  value={additionalMarkets}
                  onChange={setAdditionalMarkets}
                  options={supportedCountries.filter(
                    (c) => c !== homeMarket
                  )}
                  testid="practice-additional-markets"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Locale — collapsed by default because defaults are auto-detected */}
        <section>
          <button
            type="button"
            onClick={() => setShowLocale((v) => !v)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition"
            data-testid="practice-locale-toggle"
          >
            {showLocale ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            Locale defaults
            <span className="text-xs text-muted-foreground/70">
              · {primaryLanguage.toUpperCase()} · {timeZone} · {currency}
            </span>
          </button>

          {showLocale && (
            <div
              className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5 rounded-lg border border-border/60 bg-card/40 p-5"
              data-testid="practice-locale-panel"
            >
              <div>
                <Label className="text-sm">Primary language</Label>
                <Select
                  value={primaryLanguage}
                  onValueChange={setPrimaryLanguage}
                >
                  <SelectTrigger
                    className="mt-2"
                    data-testid="practice-primary-language"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Additional languages</Label>
                <div className="mt-2">
                  <MultiSelectChips
                    value={additionalLanguages}
                    onChange={setAdditionalLanguages}
                    options={additionalLanguageOptions}
                    testid="practice-additional-languages"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">Time zone</Label>
                <Select value={timeZone} onValueChange={setTimeZone}>
                  <SelectTrigger
                    className="mt-2"
                    data-testid="practice-timezone"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger
                    className="mt-2"
                    data-testid="practice-currency"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code} — {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </section>

        <div className="flex items-center justify-between pt-2">
          <span
            className="inline-flex items-center gap-2 text-xs text-muted-foreground"
            data-testid="autosave-indicator"
          >
            <Save className="w-3 h-3" /> Autosaved
          </span>
          <div className="flex flex-col items-end gap-1">
            <Button
              type="submit"
              size="lg"
              disabled={!canSubmit || saving}
              data-testid="onb-step2-submit"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <span className="text-xs text-muted-foreground/80">
              Next: launch your first campaign
            </span>
          </div>
        </div>
      </form>
    </OnboardingLayout>
  );
}
